import { cn } from '@/utils/cn';

/**
 * Determinate progress: pledge fulfilment, project funding, an upload.
 *
 * Overshoot is shown rather than clipped at 100%, because a project that raised more
 * than its target is news a church wants to see, and a bar silently pinned at full is
 * the same picture as one that just barely made it.
 */

export interface ProgressBarProps {
  value: number;
  max?: number;
  /** The accessible description of what is progressing. */
  label: string;
  /** Text rendered inside the bar's row, usually a formatted amount or percentage. */
  valueText?: string;
  variant?: 'brand' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const VARIANTS = {
  brand: 'bg-brand',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
} as const;

const SIZES = { sm: 'h-1.5', md: 'h-2.5', lg: 'h-4' } as const;

export const ProgressBar = ({
  value,
  max = 100,
  label,
  valueText,
  variant = 'brand',
  size = 'md',
  className,
}: ProgressBarProps) => {
  const ratio = max > 0 ? value / max : 0;
  const width = Math.min(Math.max(ratio, 0), 1) * 100;
  const exceeded = ratio > 1;

  return (
    <div className={cn('w-full', className)}>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuetext={valueText}
        className={cn(
          'w-full overflow-hidden rounded-full bg-surface-sunken',
          SIZES[size],
        )}
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500',
            exceeded ? 'bg-success' : VARIANTS[variant],
          )}
          style={{ width: `${width}%` }}
        />
      </div>

      {valueText ? (
        <p className="mt-1 text-caption tabular-nums text-fg-secondary">{valueText}</p>
      ) : null}
    </div>
  );
};
